import Events from 'events';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router';
import { Chunkosaurus } from 'chunkosaurus';
import {
  Agent,
  PubsubChannelType,
  RrwebEventAcksSerializeable,
  SalesfloorChatMessage,
  SbRrwebEvents,
  SubscribeUnsubscribeData,
  Visitor,
  VisitorInteractionUpdate,
  isChatTypingVisitorUpdate,
} from 'sb-lib/types';
import {
  ChatMessageArgs,
  ChatMessageBlockType,
  DrawEvent,
  TakeoverEvent,
  UserAccounType,
  WidgetExtent,
  isAppNotification,
} from 'types';
import { Store, store, useAppDispatch, useAppSelector } from 'shared/store/store';
import {
  callEndedDetailsChanged,
  connectServer,
  dissociateVisitor as dissociateVisitorAction,
  endDisplaydemo,
  incomingCameraStreamChanged,
  incomingDisplayStreamChanged,
  outgoingCameraStreamChanged,
  outgoingDisplayStreamChanged,
  peerConnectionErrorUpdate,
  peerStatusUpdated,
  receivedServerBuildInfo,
  selectCallStatus,
  selectFindAssociatedRealtimeVisitor,
  selectRealtimeAssociatedVisitor,
  setVisitorsCount,
  socketStatusChanged,
  visitorInteractionUpdated,
  visitorsSynced,
  visitorsUpdated,
} from 'shared/store/realtime-slice';
import {
  receivedConversationChatMessage,
  receivedConversationVisitorTypingMessage,
  sendChatMessage,
  sendChatTyping,
} from 'shared/store/chat-slice';
import {
  checkAccount,
  orgPlanUsageUpdated,
  receivedOrgAvailability,
} from 'shared/store/shared-actions';
import {
  selectActivePublicUser,
  selectActiveUser,
  selectActiveVisitor,
} from 'shared/store/shared-selectors';
import { isRealtimeVerboseLog, makeLog } from 'shared/utils/log';
import { useIsCallIntent, useOrgSocketSubscription, usePrevious } from 'shared/hooks';
import { __expose } from 'shared/utils/helpers';
import { makeErrorWithCode } from 'shared/utils/error';
import {
  SbSocketEmitActionFrontend,
  SbSocketFrontend,
  SbSocketOnEventNameFrontend,
} from './sb-socket-frontend';
import { SbPeer, SbPeerDisconnectCode, SbPeerStatus } from './sb-peer';
import { handleUnchunked } from './data-handler';
import { kits } from './kits';
import { selectActiveOrg, selectActivePublicOrg } from 'shared/store/auth-slice';
import { debounce, isEqual } from 'lodash';
import {
  DEFAULT_PROACTIVE_GREETING,
  DEFAULT_REACTIVE_GREETING,
} from 'shared/utils/constants';
import { setIsGreetingWanted } from 'shared/store/dashboard-slice';
import { handleRrwebEvent } from './rrweb-event-handler';
import { getSnapshot } from 'shared/api';
import { SbSocketErrorCode } from 'sb-lib/sb-socket-abstract';
import { getProtocolVersion } from 'sb-lib/env';
import { getStoredAuthenticationToken } from 'shared/store/storage';
import { mediaDevices } from 'shared/utils/media-devices';
import { SbMediaStreams, SbMediaStreamType } from 'sb-lib/media-streams';
import {
  receivedConversationGroupStats,
  receivedConversationStats,
  receivedConversationsUpdate,
  receivedUnreadInboxMessageCount,
  receivedVisitorConversationsUpdate,
  selectConversationGroupUsers,
} from 'shared/store/conversation-slice';
import { receivedAppNotification } from 'shared/store/app-notification-slice';
import { isSalesfloorRoom } from 'types/salesfloor';
import {
  removeSalesfloorRoom,
  updateSalesfloorRoom,
  receivedConversationChatMessage as salesfloorReceivedConversationChatMessage,
  fetchAllRooms,
} from 'shared/store/salesfloor-slice';
import { addDialSession, removeDialSession } from 'shared/store/livelisten-slice';
import { setGeneratedAnswer } from 'shared/store/ai-chat-slice';

const log = makeLog('manager');
const IS_LOG_VERBOSE = isRealtimeVerboseLog();

export type PeerStreamChange = {
  streamId: null | string;
  isReceivingVideo: boolean;
  isReceivingAudio: boolean;
};

const RECENT_DISCONNECT_TIMEOUT = 1000;
const NO_RECONNECT_CODES = [
  SbSocketErrorCode.DISPLACED,
  SbSocketErrorCode.BAD_ACCOUNT,
  SbSocketErrorCode.BLOCKED,
  SbSocketErrorCode.EXISTING_USER_SOCKET_BUSY,
  SbSocketErrorCode.PASSIVE_REJECTION,
  SbSocketErrorCode.CONNECTION_DATA_NOT_FOUND,
];

const chunkosaurus = new Chunkosaurus();
const sbSocket = new SbSocketFrontend({
  logger: makeLog('sb-socket-frontend'),
  url: process.env.WS_URL || '',
  protocolVersion: getProtocolVersion(),
  reconnectWhenOffline: true,
});
const sbMediaStreams = new SbMediaStreams(mediaDevices);
const sbPeer = new SbPeer(sbMediaStreams);

export const RealtimeManager = () => {
  useRealtime();
  return null;
};

/****************************************************
 *
 *              Realtime Manager Hooks
 *
 * Perform realtime related actions on state changes.
 ****************************************************/
export const useRealtime = () => {
  useInitializeRealtimeEffect();
  useAuthenticationEffect();
  useReconnectSocketEffect();
  useConnectSocketOnLocationChangeEffect();
  useRefreshUserOnSocketError();
  useSendInteractionUpdate();
  useChatGreetingSender();
  useOrgSocketSubscription(
    PubsubChannelType['chat.inbox.conversation-updates.open.all'],
    true,
    false,
  );
  kits.forEach(k => k.useKitHook());
};

/**
 * Initialize SbSocket, SbPeer, and Chunkosaurus when ready
 */
const useInitializeRealtimeEffect = () => {
  const storeRef = useRef(useStore());
  const curAuthed = useAppSelector(selectActiveUser);
  const curAuthedPublic = useAppSelector(selectActivePublicUser);
  const [initialized, setInitialized] = useState(false);
  useEffect(
    function initializeUseRealtime() {
      if (initialized || (!curAuthed && !curAuthedPublic)) return;
      initSocket(storeRef.current);
      initMediaStreams(storeRef.current);
      initPeer(storeRef.current);
      chunkosaurus.on('data', chunk => handleUnchunked(chunk));
      setInitialized(true);
    },
    [curAuthed, curAuthedPublic, initialized],
  );
};

/**
 * React to agent auth, de-auth, and user changes.
 */
const useAuthenticationEffect = () => {
  const dispatch = useDispatch();
  const isConnectedToServer = useAppSelector(s => s.realtime.isConnectedToServer);
  const isConnectServerAction = useAppSelector(s => s.realtime.isConnectServerAction);
  const curAuthed = useAppSelector(selectActiveUser);
  const curAuthedPublic = useAppSelector(selectActivePublicUser);
  const prevAuthed = usePrevious(curAuthed);
  const prevAuthedPublic = usePrevious(curAuthedPublic);
  const isCallIntent = useIsCallIntent();

  // connect
  useEffect(
    function authedEffect() {
      const loggedIn =
        (!prevAuthed && curAuthed) || (!prevAuthedPublic && curAuthedPublic);
      const changedUser =
        (prevAuthed && curAuthed && prevAuthed.id !== curAuthed.id) ||
        (prevAuthedPublic &&
          curAuthedPublic &&
          prevAuthedPublic.id !== curAuthedPublic.id) ||
        // switching from public to regular
        (prevAuthedPublic && curAuthedPublic && !prevAuthed && curAuthed);
      if (
        !isConnectServerAction &&
        (curAuthed || curAuthedPublic) &&
        (loggedIn || changedUser)
      ) {
        dispatch(
          connectServer({
            passive: true,
            kickOther: isCallIntent ? true : false,
            reason: `${loggedIn ? 'new login' : 'user change'}${
              isCallIntent ? '(kicking others for ?call)' : ''
            }`,
          }),
        );
        if (curAuthed) {
          dispatch(
            orgPlanUsageUpdated({
              orgProactiveCallsRemaining: curAuthed.orgUsage.remainingProactiveCalls,
              orgReactiveCallsRemaining: curAuthed.orgUsage.remainingReactiveCalls,
              orgTakeoversRemaining: curAuthed.orgUsage.remainingTakeovers,
              orgSpyViewingsRemaining: curAuthed.orgUsage.remainingSpyViewings,
              orgSixsenseCompanyDetailFetchesRemaining:
                curAuthed.orgUsage.remainingSixsenseCompanyDetailFetches,
            }),
          );
        }
      }
    },
    [
      curAuthed,
      curAuthedPublic,
      prevAuthed,
      prevAuthedPublic,
      dispatch,
      isConnectServerAction,
      isCallIntent,
    ],
  );

  // disconnect
  useEffect(
    function unauthedEffect() {
      if (!curAuthed && !curAuthedPublic && isConnectedToServer) {
        disconnectSocket('unauthed');
      }
    },
    [curAuthed, curAuthedPublic, isConnectedToServer],
  );
};

/**
 * Attempt socket connect on page change if not connected.
 */
const useConnectSocketOnLocationChangeEffect = () => {
  const dispatch = useDispatch();
  const isConnectedToServer = useAppSelector(s => s.realtime.isConnectedToServer);
  const isConnectServerAction = useAppSelector(s => s.realtime.isConnectServerAction);
  const curAuthed = useAppSelector(selectActiveUser);
  const pathname = useLocation().pathname;
  const prevPathname = usePrevious(pathname);
  const isPathChanged = prevPathname !== undefined && pathname !== prevPathname;
  useEffect(
    function connectSocketOnLocationChangeEffect() {
      if (!isConnectedToServer && !isConnectServerAction && curAuthed && isPathChanged) {
        log.debug(`Not connected and page changed ${pathname}, will attempt connect`);
        dispatch(
          connectServer({ passive: true, kickOther: false, reason: 'page changed' }),
        );
      }
    },
    [
      curAuthed,
      isConnectedToServer,
      isConnectServerAction,
      isPathChanged,
      pathname,
      dispatch,
    ],
  );
};

/**
 * Socket reconnect
 */
const useReconnectSocketEffect = () => {
  const dispatch = useDispatch();
  const {
    serverErrorCode,
    isConnectedToServer,
    isConnectingToServer,
    isConnectServerAction,
    lastServerDisconnectTime,
  } = useAppSelector(s => s.realtime);
  useEffect(
    function reconnectSocketEffect() {
      const lastConnectedAgo = Date.now() - lastServerDisconnectTime;
      const wasRecentlyConnected = lastConnectedAgo < RECENT_DISCONNECT_TIMEOUT;
      if (
        !isConnectedToServer &&
        serverErrorCode &&
        !NO_RECONNECT_CODES.includes(serverErrorCode) &&
        wasRecentlyConnected &&
        !isConnectServerAction &&
        !isConnectingToServer
      ) {
        log.debug(
          `Automatically attempting a reconnect because we were connected ${lastConnectedAgo}ms ago (serverErrorCode=${serverErrorCode})`,
        );
        dispatch(
          connectServer({
            passive: true,
            kickOther: false,
            reason: 'auto-reconnect on unexpected disconnect',
          }),
        );
      }
    },
    [
      serverErrorCode,
      isConnectedToServer,
      isConnectingToServer,
      isConnectServerAction,
      lastServerDisconnectTime,
      dispatch,
    ],
  );
};

/**
 * If the server disconnects the user for a bad account,
 * refresh from REST. Effects will attempt a reconnect
 * if appropriate.
 */
const useRefreshUserOnSocketError = () => {
  const dispatch = useDispatch();
  const { serverErrorCode, serverStatus, hasCheckedBadAccount } = useAppSelector(
    s => s.realtime,
  );
  const curAuthed = useAppSelector(selectActiveUser);
  useEffect(
    function refreshUserOnSocketError() {
      if (
        serverStatus === 'disconnected' &&
        curAuthed &&
        serverErrorCode === SbSocketErrorCode.BAD_ACCOUNT &&
        !hasCheckedBadAccount
      ) {
        log.debug(
          `Socket connect failed for bad account, will refresh auth information to see if that helps`,
        );
        dispatch(checkAccount());
      }
    },
    [curAuthed, hasCheckedBadAccount, serverErrorCode, serverStatus, dispatch],
  );
};

/**
 * Sends interaction updates whenever state changes.
 */
const useSendInteractionUpdate = () => {
  const dispatch = useDispatch();
  const realtimeAssociatedVisitor = useAppSelector(selectRealtimeAssociatedVisitor);
  const { optimisticInteractions, isConnectedToServer } = useAppSelector(s => s.realtime);
  const updateBeingSent = useRef<VisitorInteractionUpdate>();

  const activeAssociatedVisitorId = realtimeAssociatedVisitor?.sbid;

  useEffect(() => {
    if (!isConnectedToServer || !activeAssociatedVisitorId) return;
    if (!Object.keys(optimisticInteractions).length) return;
    if (isEqual(optimisticInteractions, updateBeingSent.current)) return;

    // Send update on slight timeout to allow cascading actions to roll up into a single call.
    log.debug(
      `Preparing to send interaction update in 50ms: ${JSON.stringify(
        optimisticInteractions,
      )}`,
    );
    updateBeingSent.current = optimisticInteractions;
    const timeout = setTimeout(() => {
      sendInteractionUpdate(activeAssociatedVisitorId, optimisticInteractions)
        .then(res => {
          if (!res || !res.data) {
            log.warn(
              'Interaction update failed, not resetting optimistic interaction state',
            );
            return;
          }
          dispatch(visitorInteractionUpdated(res.data));
        })
        .catch(err => {
          log.warn(`Failed to send interaction update: ${err}`);
        })
        .finally(() => {
          updateBeingSent.current = undefined;
        });
    }, 50);
    return () => {
      updateBeingSent.current = undefined;
      clearTimeout(timeout);
    };
  }, [optimisticInteractions, activeAssociatedVisitorId, isConnectedToServer, dispatch]);
};

/**
 * Manages sending greetings to visitors when a call starts and a greeting
 * is wanted.
 */
const useChatGreetingSender = () => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectActiveOrg);
  const activeVisitor = useAppSelector(selectActiveVisitor);
  const isGreetingWanted = useAppSelector(s => s.dashboard.isGreetingWanted);
  const callStatus = useAppSelector(selectCallStatus);
  const isConnectedToPeer = useAppSelector(s => s.realtime.isConnectedToPeer);
  const hasGreetedRef = useRef(false);
  const { conversation } = useAppSelector(s => s.conversation);

  const greeting = activeVisitor?.hasDialed
    ? org?.customReactiveGreeting || DEFAULT_REACTIVE_GREETING
    : org?.customProactiveGreeting || DEFAULT_PROACTIVE_GREETING;

  let shouldGreet = isGreetingWanted || activeVisitor?.wantsChat;
  if (!shouldGreet) {
    shouldGreet = activeVisitor?.hasLoaded
      ? !!org?.autoSendReactiveGreeting
      : !!org?.autoSendProactiveGreeting;
  }
  shouldGreet = shouldGreet && callStatus === 'active' && isConnectedToPeer;

  // Send greeting if call has started and we want to greet
  useEffect(() => {
    // Prevent double-greeting
    if (!shouldGreet || hasGreetedRef.current || !conversation?.id) return;
    hasGreetedRef.current = true;

    // Expand chat and start typing indicator first
    const timeout1 = setTimeout(() => {
      sendChatExpandedToVisitor(true);
      dispatch(sendChatTyping({ isTyping: true, conversationId: conversation.id }));
    }, 1500);

    // Send greeting shortly after
    const timeout2 = setTimeout(() => {
      dispatch(
        sendChatMessage({
          visitorId: conversation.visitor.id,
          conversationId: conversation.id,
          content: {
            blocks: [
              { type: ChatMessageBlockType.TEXT, text: greeting },
              { type: ChatMessageBlockType.ANSWER_BUTTONS },
            ],
          },
        }),
      );
      // Reset explicit intent of whether to greet or not.
      dispatch(setIsGreetingWanted(null));
    }, 3500);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    };
  }, [shouldGreet, greeting, dispatch, conversation]);

  // Reset has greeted on visitor change
  const activeVisitorId = activeVisitor?.id;
  useEffect(() => {
    hasGreetedRef.current = false;
  }, [activeVisitorId]);
};

/****************************************************
 *
 *                 Management
 *
 * Bridge from Redux to SbSocket and SbPeer.
 ****************************************************/

const initSocket = (store: Store) => {
  log.debug(`Initialize socket`);
  const state = store.getState();
  const org = selectActiveOrg(state) || selectActivePublicOrg(state);
  if (!org) {
    throw new Error('Attempted to initialize socket before org data received');
  }

  const OE = SbSocketOnEventNameFrontend;

  sbSocket.on(OE.status, status => store.dispatch(socketStatusChanged(status)));
  sbSocket.on(OE.visitorsSync, list => store.dispatch(visitorsSynced(list.visitors)));
  sbSocket.on(OE.visitorsCount, data => store.dispatch(setVisitorsCount(data)));
  sbSocket.on(OE.visitorUpdates, updates => store.dispatch(visitorsUpdated(updates)));
  sbSocket.on(OE.rrwebEvent, ({ data: { clientSid, agentSid, content } }) => {
    const selfSid = store.getState().realtime.sid;
    const logExtra = {
      clientSid,
      agentSid,
      chunk: `${content.id.substr(0, 8)}..:${content.chunk}/${content.chunks}, ${
        content.data.length
      }chars`,
    };
    if (selfSid !== agentSid) {
      log.warn(
        `Got data for sid "${agentSid}" from ${clientSid}, but agent sid is "${selfSid}", might be early`,
        logExtra,
      );
    }
    chunkosaurus?.feedChunk(content);
  });
  sbSocket.on(OE.rrwebSnapshotTicket, async ({ ticket }) => {
    const startDownload = Date.now();
    const res = await getSnapshot(ticket);
    const downloadTime = Math.round((Date.now() - startDownload) / 100) / 10;
    const snapshot = res.data;
    const rids = Array.isArray(snapshot)
      ? snapshot.reduce((acc, env) => `${acc}, ${env.event.rid}:${env.nonce}`, '')
      : `${snapshot.event.rid}:${snapshot.nonce}`;
    log.debug(
      `Downloaded snapshot in ${downloadTime}s (ticket=${ticket}, rid(s)=${rids})`,
    );

    const envelopes = Array.isArray(snapshot) ? snapshot : [snapshot];
    envelopes.forEach(env => handleRrwebEvent(env.event, env.nonce));
  });
  sbSocket.on(OE.buildInformation, info => store.dispatch(receivedServerBuildInfo(info)));
  sbSocket.on(OE.availability, data =>
    store.dispatch(
      receivedOrgAvailability({
        available: data.available,
        availableAdminIds: data.admins,
      }),
    ),
  );

  sbSocket.on(OE.updateUsage, usage =>
    store.dispatch(
      orgPlanUsageUpdated({
        orgProactiveCallsRemaining: usage.remainingProactiveCalls,
        orgReactiveCallsRemaining: usage.remainingReactiveCalls,
        orgTakeoversRemaining: usage.remainingTakeovers,
        orgSpyViewingsRemaining: usage.remainingSpyViewings,
        orgSixsenseCompanyDetailFetchesRemaining:
          usage.remainingSixsenseCompanyDetailFetches,
      }),
    ),
  );

  sbSocket.on(OE.destroyActivePeer, payload => {
    const associatedVisitor = selectFindAssociatedRealtimeVisitor(store.getState());
    if (associatedVisitor) {
      store.dispatch(
        dissociateVisitorAction({
          isKick: payload.code === 'ADMIN_KICK',
          sbid: associatedVisitor.sbid,
        }),
      );
    }
  });

  sbSocket.on(OE.callEndedDetails, payload => {
    store.dispatch(callEndedDetailsChanged(payload));
  });

  sbSocket.on(OE.pubsubMessage, payload => {
    const { channelType, channelIdentifier, message } = payload;
    switch (channelType) {
      case PubsubChannelType['chat.inbox.unread-message-count.user']:
        void store.dispatch(receivedUnreadInboxMessageCount(message));
        break;

      case PubsubChannelType['chat.inbox.stats.org']:
      case PubsubChannelType['chat.inbox.stats.user']:
        void store.dispatch(receivedConversationStats(message));
        break;
      case PubsubChannelType['chat.inbox.stats.group']:
        void store.dispatch(receivedConversationGroupStats(message));
        break;

      case PubsubChannelType['chat.inbox.conversation-updates.open.assigned']:
      case PubsubChannelType['chat.inbox.conversation-updates.open.mentioned']:
      case PubsubChannelType['chat.inbox.conversation-updates.open.all']:
      case PubsubChannelType['chat.inbox.conversation-updates.open.unassigned']:
      case PubsubChannelType['chat.inbox.conversation-updates.closed.assigned']:
      case PubsubChannelType['chat.inbox.conversation-updates.closed.mentioned']:
      case PubsubChannelType['chat.inbox.conversation-updates.closed.all']:
      case PubsubChannelType['chat.inbox.conversation-updates.closed.unassigned']:
        if (
          store.getState().conversation.conversationsGroupId &&
          ((payload.message.data.assigneeId &&
            !selectConversationGroupUsers(state)?.includes(
              payload.message.data.assigneeId,
            )) ||
            payload.message.data.assignedGroupId !==
              store.getState().conversation.conversationsGroupId)
        ) {
          break;
        }
        void store.dispatch(receivedConversationsUpdate(payload));
        break;

      case PubsubChannelType['chat.conversations.visitor']:
        void store.dispatch(receivedVisitorConversationsUpdate(payload));
        break;

      case PubsubChannelType['chat.conversation']: {
        const { id: userId } = selectActiveUser(store.getState()) || {};
        if (isChatTypingVisitorUpdate(payload.message)) {
          // Visitor typing state
          void store.dispatch(receivedConversationVisitorTypingMessage(payload.message));
        } else if (userId !== payload.message.from) {
          // Chat message update from visitor or other agent
          void store.dispatch(receivedConversationChatMessage(payload));
        }
        break;
      }

      case PubsubChannelType['app-notifications.user']: {
        if (isAppNotification(payload.message)) {
          void store.dispatch(receivedAppNotification(payload.message));
        } else {
          log.info(
            `Received unexpected payload for ['app-notifications.user'] subscription`,
            payload.message,
          );
        }
        break;
      }

      case PubsubChannelType['salesfloor.room']: {
        if (isSalesfloorRoom(payload.message)) {
          void store.dispatch(updateSalesfloorRoom(payload.message));
        } else if (payload.message === null) {
          void store.dispatch(removeSalesfloorRoom({ roomId: channelIdentifier }));
        } else {
          log.warn(
            `Received unexpected payload for ['salesfloor.room'] subscription`,
            payload.message,
          );
        }
        break;
      }

      case PubsubChannelType['salesfloor.conversation.dm']:
      case PubsubChannelType['salesfloor.conversation.room']: {
        const state = store.getState();
        const user = selectActiveUser(state);
        const publicUser = selectActivePublicUser(state);
        const conversationUser = user ? user : publicUser;
        if (!conversationUser) {
          log.warn('Could not resolve active user');
          return;
        }
        log.debug(
          `received salesfloor conversation event, USER: ${conversationUser.id}`,
          payload,
        );
        const update = {
          ...payload,
          ownUser: {
            id: conversationUser.id,
            type: user ? UserAccounType.REGULAR : UserAccounType.PUBLIC,
          },
        };
        void store.dispatch(salesfloorReceivedConversationChatMessage(update));
        break;
      }

      case PubsubChannelType['livelisten.dial_session.new']: {
        log.debug('Dial Session WS update:', payload.message);
        void store.dispatch(addDialSession(payload.message));

        // Ensure that the room participants include the dial session user
        void store.dispatch(fetchAllRooms());
        break;
      }

      case PubsubChannelType['livelisten.dial_session.ended']: {
        log.debug('Dial Session WS removal:', payload.message);
        void store.dispatch(removeDialSession(payload.message));
        void store.dispatch(fetchAllRooms());
        break;
      }

      case PubsubChannelType['ai-chatbot.faq.generated_answer']: {
        void store.dispatch(setGeneratedAnswer(payload.message));
        break;
      }

      default:
        break;
    }
  });

  __expose({ sbSocket });
};

export const connectSocket = async (
  orgId: number,
  kickOther = false,
  passive = false,
  reason: string,
) => {
  const authenticationToken = getStoredAuthenticationToken();
  if (!authenticationToken) {
    // we should never attempt a socket connection without having authenticated, but just in case...
    log.warn(`Attempted connectSocket without auth token`);
    return;
  }
  log.debug(
    `Connect socket orgId: [${orgId}] reason: [${reason}] kickOther: [${kickOther}] passive: [${passive}]`,
  );
  sbSocket.connect({
    kickOther,
    passive,
    orgId,
    authenticationToken,
  });
};

export const introduceSocket = async () => {
  if (!sbSocket) {
    throw new Error('Cannot introduce without sbSocket');
  }
  return sbSocket.emit(SbSocketEmitActionFrontend.introduction, {});
};

export const disconnectSocket = (reason = 'none') => {
  log.debug(`Disconnect socket (${reason})`);
  sbSocket.disconnect();
};

export const associateVisitor = async (visitor: Visitor) => {
  if (!sbSocket) {
    throw makeErrorWithCode(
      'Cannot associate visitor without sbSocket',
      'NO_SB_SOCKET',
      'AssociateVisitorError',
    );
  }
  try {
    await sbSocket.emit(SbSocketEmitActionFrontend.associateVisitor, {
      sbid: visitor.sbid,
    });
    log.debug(`Association with visitor ${visitor.sbid} was successful`);
  } catch (e) {
    const msg = `Association with visitor ${visitor.sbid} failed: "${e}"`;
    throw makeErrorWithCode(msg, e, 'AssociateVisitorError');
  }
};

export const dissociateVisitor = async (sbid: string) => {
  if (!sbSocket) return '';
  await sbSocket.emit(SbSocketEmitActionFrontend.dissociateVisitor, { sbid });
  log.debug(`Dissociated from visitor (sbid=${sbid})`);
};

export const kickAgentFromVisitor = (agentId: number, visitorId: string) => {
  log.debug(`Kicking agent ${agentId} from visitor ${visitorId}`);
  void sbSocket.emit(SbSocketEmitActionFrontend.kickAgent, { agentId, visitorId });
};

export const sendSocketChatMessage = async (data: ChatMessageArgs) => {
  log.debug(
    `sendSocketChatMessage, conversation: ${data.conversationId} visitor: ${data.visitorId}`,
  );
  return sbSocket.emit(SbSocketEmitActionFrontend.chatMessage, data);
};

export const sendSocketChatTyping = (
  isTyping: boolean,
  conversationId: number,
  agent: Agent,
) => {
  sbSocket.emit(SbSocketEmitActionFrontend.chatTyping, {
    isTyping,
    isAI: false,
    conversationId,
    agent,
  });
};

const sendSocketReadChatMessages = (chatMessageIds: string[]) => {
  sbSocket.emit(SbSocketEmitActionFrontend.readChatMessages, { chatMessageIds });
};

export const sendSocketReadChatMessage = (() => {
  let merged = {};

  const debounced = debounce(() => {
    sendSocketReadChatMessages(Object.keys(merged));
    merged = {};
  }, 2000);

  return (obj: Record<string, boolean>) => {
    merged = { ...merged, ...obj };
    debounced();
  };
})();

export const sendRrwebEventAcks = (acks: RrwebEventAcksSerializeable) => {
  const visitor = selectRealtimeAssociatedVisitor(store.getState());
  const { isConnectedToServer, isConnectedToPeer } = store.getState().realtime;
  const canSend = visitor && visitor.sid && (isConnectedToServer || isConnectedToPeer);
  if (!canSend) return false;
  if (isConnectedToPeer && sbPeer.isDataChannelOpen()) {
    sbPeer.sendRrwebEventAcks(visitor.sid, acks);
    if (IS_LOG_VERBOSE) {
      log.debug(`Sent rrweb event acks through WebRTC: ${JSON.stringify(acks)}`);
    }
  } else if (isConnectedToServer) {
    sbSocket.emit(SbSocketEmitActionFrontend.rrwebEventAcks, {
      visitorSid: visitor.sid,
      acks,
    });
    if (IS_LOG_VERBOSE) {
      log.debug(`Sent rrweb event acks through socket: ${JSON.stringify(acks)}`);
    }
  }
  return true;
};

export const sendInteractionUpdate = async (
  sbid: string,
  update: VisitorInteractionUpdate,
) => {
  const updateStr = JSON.stringify(update);
  if (!sbSocket) {
    log.warn(
      `Attempted to send interaction update ${updateStr} without socket, ignoring`,
    );
    return;
  }
  if (!sbid) {
    log.warn(`Attempted to send interaction update ${updateStr} without sbid, ignoring`);
    return;
  }
  const associatedVisitor = selectRealtimeAssociatedVisitor(store.getState());
  if (!associatedVisitor) {
    log.warn(
      `Attemtped to send interaction update ${updateStr} without being associated, ignoring`,
    );
    return;
  }
  if (associatedVisitor && associatedVisitor.sbid !== sbid) {
    log.warn(`Attempted to send interaction update ${updateStr} to wrong sbid, ignoring`);
    return;
  }
  log.info(`Sending interaction update ${updateStr} for ${sbid}`);
  return sbSocket.emit(SbSocketEmitActionFrontend.updateVisitorInteraction, {
    sbid,
    ...update,
  });
};

export const sendSocketSalesfloorChatMessage = async (data: SalesfloorChatMessage) => {
  log.debug(
    `sendSocketSalesfloorChatMessage, conversation: ${data.conversationId} sender: ${data.sender.id}/${data.sender.displayName}`,
  );
  return sbSocket.emit(SbSocketEmitActionFrontend.salesfloorChatMessage, data);
};

export const subscribeToChannel = ({
  channelType,
  channelIdentifier,
}: SubscribeUnsubscribeData) => {
  return sbSocket.emit(SbSocketEmitActionFrontend.subscribe, {
    channelType,
    channelIdentifier,
  });
};

export const unsubscribeFromChannel = ({
  channelType,
  channelIdentifier,
}: SubscribeUnsubscribeData) => {
  return sbSocket.emit(SbSocketEmitActionFrontend.unsubscribe, {
    channelType,
    channelIdentifier,
  });
};

export const relayRrwebReset = (visitorSid: string) => {
  return sbSocket.emit(SbSocketEmitActionFrontend.visitorRelay, {
    visitorSid,
    data: { type: 'rrweb-reset' },
  });
};

export const resetSpyOptin = (sbid: string) => {
  return sbSocket.emit(SbSocketEmitActionFrontend.resetSpyOptin, { sbid });
};

const initMediaStreams = (store: Store) => {
  const { dispatch } = store;
  sbMediaStreams.onStreamChange(data => {
    if (data.streamType === SbMediaStreamType.camera) {
      dispatch(outgoingCameraStreamChanged(data.stream?.id || null));
    } else if (data.streamType === SbMediaStreamType.display) {
      store.dispatch(outgoingDisplayStreamChanged(data.stream?.id || null));
      if (!data.stream?.id) {
        store.dispatch(endDisplaydemo());
      }
    }
  });
};

const initPeer = async (store: Store) => {
  const { dispatch } = store;
  sbPeer.onStatus(status => {
    log.debug(`Peer status "${status}"`);
    analyzeConnectionState(status);
    dispatch(peerStatusUpdated(status));
  });
  sbPeer.onDisconnect(code => {
    if (code === SbPeerDisconnectCode.CLOSED) {
      log.debug('Remote peer disconnected');
    }
  });
  sbPeer.onData(data => {
    chunkosaurus?.feedChunk(data);
  });
  sbPeer.onError(msg => {
    log.warn(`Peer error: ${msg}`);
    const m = msg.toLowerCase();
    if (m === 'transport channel closed') return;
    if (m.includes('user initiated abort')) return;
    if (m.includes('user-initiated abort')) return;
    store.dispatch(peerConnectionErrorUpdate(msg));
  });
  sbPeer.onStreamInChange(data => {
    const { type, stream, isReceivingAudio, isReceivingVideo } = data;
    const action = ({
      [SbMediaStreamType.camera]: incomingCameraStreamChanged,
      [SbMediaStreamType.display]: incomingDisplayStreamChanged,
    } as const)[type];
    dispatch(
      action({
        streamId: stream?.id || null,
        isReceivingAudio,
        isReceivingVideo,
      }),
    );
  });
  __expose({ sbPeer });
};

const startTwilioVideo = async () => {
  let res;
  for (let i = 0; i < 5; i++) {
    try {
      res = await sbSocket.emit(SbSocketEmitActionFrontend.webRtcConnection);
      break;
    } catch (e) {
      if (e === 'NO_VISITOR_DATA') {
        log.warn('Got NO_VISITOR_DATA from webrtc_connection. Will retry in 2s.');
        await new Promise(r => setTimeout(r, 2000));
      } else {
        log.error(e);
        break;
      }
    }
  }

  if (!res) {
    store.dispatch(
      peerConnectionErrorUpdate('Unable to obtain auth token - visitor not associated.'),
    );
    store.dispatch(peerStatusUpdated('disconnected'));
    return;
  }

  sbPeer.connect(res.token, res.room);
};

export const startPeer = async (getState: Store['getState']) => {
  log.debug('Peer starting...');
  analyzeConnectionState('connecting');

  const activeVisitorId = getState().dashboard.activeVisitorId;
  if (!activeVisitorId) {
    return null;
  }
  const activeVisitor = getState().realtime.visitorMap[activeVisitorId];
  if (!activeVisitor) {
    return null;
  }
  await startTwilioVideo();
};

export const stopPeer = async () => {
  log.debug('Disconnect peer');
  logConnectionState();
  sbPeer.disconnect();
};

enum ConnectionState {
  ok = 'ok',
  unable_to_connect = 'unable_to_connect',
  error_recovered = 'error_recovered',
  error_unrecovered = 'error_unrecovered',
}

let sessionConnectionState: {
  failedAttempts: number;
  state: ConnectionState;
} | null = null;

export const analyzeConnectionState = (status: SbPeerStatus) => {
  // Valid state transitions
  // ok -> error_unrecovered
  // unable_to_connect -> ok, error_recovered
  // error_recovered -> error_unrecovered
  // error_unrecovered -> error_recovered

  let state: ConnectionState | null = null;
  let failedAttempts = 0;

  if (!sessionConnectionState) {
    if (status === 'connecting') {
      state = ConnectionState.unable_to_connect;
    } else {
      return;
    }
  } else {
    state = sessionConnectionState.state;

    if (status === 'disconnected') {
      failedAttempts = sessionConnectionState.failedAttempts + 1;
    }

    if (sessionConnectionState.state === ConnectionState.ok) {
      if (status == 'disconnected') {
        state = ConnectionState.error_unrecovered;
      }
    } else if (sessionConnectionState.state === ConnectionState.unable_to_connect) {
      if (status === 'connected') {
        if (sessionConnectionState.failedAttempts === 0) {
          state = ConnectionState.ok;
        } else {
          state = ConnectionState.error_recovered;
        }
      }
    } else if (sessionConnectionState.state === ConnectionState.error_recovered) {
      if (status === 'disconnected') {
        state = ConnectionState.error_unrecovered;
      }
    } else if (sessionConnectionState.state === ConnectionState.error_unrecovered) {
      if (status === 'connected') {
        state = ConnectionState.error_recovered;
      }
    }
  }
  sessionConnectionState = { failedAttempts: failedAttempts, state: state };
  log.debug('Session conn state:', sessionConnectionState);
};

export const logConnectionState = () => {
  if (!sessionConnectionState) {
    return;
  }

  log.info(`*** PeerConnection state ***: ${sessionConnectionState.state}`);
  sessionConnectionState = null;
};

export const sendTakeoverEventToVisitor = (event: TakeoverEvent) => {
  sbPeer.sendTakeoverEvent(event);
};

export const sendDrawEventToVisitor = (event: DrawEvent) => {
  sbPeer.sendDrawEvent(event);
};

export const sendWidgetExtentToVisitor = (extent: WidgetExtent) => {
  sbPeer.sendWidgetExtent({
    type: 'widgetExtent',
    extent,
  });
};

export const sendChatExpandedToVisitor = (expanded: boolean) => {
  sbPeer.sendChatExpanded({
    type: 'chatExpanded',
    expanded,
  });
};

export const sendEndCall = () => {
  sbPeer.sendEndCall();
};

export const getOutgoingCameraStream = () => {
  return sbMediaStreams.getCameraStream();
};

export const startMediaStream = async () => {
  await sbMediaStreams.startCameraStream({ audio: true, video: true });
};

export const stopMediaStream = () => {
  sbMediaStreams.stopCameraStream();
};

export const setMediaDevices = async (audioDeviceId?: string, videoDeviceId?: string) => {
  await sbMediaStreams.startCameraStream({
    audio: audioDeviceId || true,
    video: videoDeviceId || true,
  });
};

export const sendFeDashboardState = (
  args?: Parameters<SbPeer['sendFeDashboardState']>[0],
) => {
  sbPeer.sendFeDashboardState(args);
};

export const getIncomingCameraStream = () => {
  return sbPeer.getIncomingCameraStream() || null;
};

export const getIncomingDisplayStream = () => {
  return sbPeer.getIncomingDisplayStream() || null;
};

export const startOutgoingDisplayStream = async () => {
  await sbMediaStreams.startDisplayStream();
};

export const stopOutgoingDisplayStream = () => {
  sbMediaStreams.stopDisplayStream();
};

export const getOutgoingDisplayStream = () => {
  return sbMediaStreams.getDisplayStream();
};

// info: https://github.com/GetServiceBell/ServiceBell/pull/2193
let incomingVideoStream = null as null | MediaStream;
export const getIncomingVideoStream = () => {
  const incomingStream = sbPeer.getIncomingCameraStream();
  if (!incomingStream) {
    return null;
  }
  const videoTrack = incomingStream.getVideoTracks()[0];
  if (!videoTrack) {
    return null;
  }
  if (
    incomingVideoStream &&
    incomingVideoStream.getVideoTracks()[0]?.id === videoTrack.id
  ) {
    return incomingVideoStream;
  }
  incomingVideoStream = new MediaStream();
  incomingVideoStream.addTrack(videoTrack);
  return incomingVideoStream;
};

// info: https://github.com/GetServiceBell/ServiceBell/pull/2193
let incomingAudioStream = null as null | MediaStream;
export const getIncomingAudioStream = () => {
  const incomingStream = sbPeer.getIncomingCameraStream();
  if (!incomingStream) {
    return null;
  }
  const audioTrack = incomingStream.getAudioTracks()[0];
  if (!audioTrack) {
    return null;
  }
  if (
    incomingAudioStream &&
    incomingAudioStream.getAudioTracks()[0]?.id === audioTrack.id
  ) {
    return incomingAudioStream;
  }
  incomingAudioStream = new MediaStream();
  incomingAudioStream.addTrack(audioTrack);
  return incomingAudioStream;
};

export const setOutgoingAudioEnabled = (isEnabled: boolean) => {
  sbPeer.setOutgoingAudioEnabled(isEnabled);
  sendFeDashboardState();
};
export const setOutgoingVideoEnabled = (isEnabled: boolean) => {
  sbPeer.setOutgoingVideoEnabled(isEnabled);
  sendFeDashboardState();
};

/****************************************************
 *
 *                 Manager event emitter
 *
 * Stream-like events not suitable for redux.
 ****************************************************/
export type ManagerEvent = 'rrweb' | 'rrwebwidget';
export type ManagerHandler = (event: ManagerEvent, payload: any) => void;
export type ManagerOn = (event: ManagerEvent, cb: (...args: any[]) => any) => void;
interface ManagerEmitter extends Events.EventEmitter {
  on(event: ManagerEvent, listener: (...args: any[]) => void): this;
  off(event: ManagerEvent, listener: (...args: any[]) => void): this;
  emit(event: ManagerEvent, ...args: any[]): boolean;
}

const managerEmitter = new Events.EventEmitter() as ManagerEmitter;

export const emitRrwebEventFromVisitor = (event: SbRrwebEvents) => {
  managerEmitter.emit('rrweb', event);
};

export const emitRrwebWidgetEventFromVisitor = (event: SbRrwebEvents) => {
  managerEmitter.emit('rrwebwidget', event);
};

export const subscribeToRrweb = (cb: (event: SbRrwebEvents) => void) => {
  managerEmitter.on('rrweb', cb);
};
export const unsubscribeToRrweb = (cb: (event: SbRrwebEvents) => void) => {
  managerEmitter.off('rrweb', cb);
};

export const subscribeToWidgetRrweb = (cb: (event: SbRrwebEvents) => void) => {
  managerEmitter.on('rrwebwidget', cb);
};
export const unsubscribeToWidgetRrweb = (cb: (event: SbRrwebEvents) => void) => {
  managerEmitter.off('rrwebwidget', cb);
};
